// Sorting and filtering for the V3 tables, with no React. A column is
//   { key, label, text(row) -> the text shown (and searched), sort(row) -> a number or text to sort by (default: the text), options }
// options: true gives the column's filter a list of the values it has, for a column with few different values (severity, capability); order lists
// those values in the order to show them.

const blank = (v) => v === null || v === undefined || v === '';

const compare = (a, b) => {
  if (blank(a) && blank(b)) return 0;
  if (blank(a)) return 1;                                   // empty values last, whichever way it is sorted
  if (blank(b)) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
};

// sort: { key, dir } with dir 'asc' or 'desc', or null for the order the rows came in.
export const sortRows = (rows, columns, sort) => {
  const column = sort && columns.find((c) => c.key === sort.key);
  if (!column) return rows;
  const value = column.sort || column.text;
  const sign = sort.dir === 'desc' ? -1 : 1;
  return rows
    .map((row, index) => ({ row, index, v: value(row) }))
    .sort((x, y) => {
      if (blank(x.v) || blank(y.v)) return compare(x.v, y.v) || x.index - y.index;
      return sign * compare(x.v, y.v) || x.index - y.index;   // a stable sort: equal rows keep their order
    })
    .map((x) => x.row);
};

// filters: { key: text }. A text column keeps rows that contain the text (any case); an options column keeps rows that are exactly the value.
export const filterRows = (rows, columns, filters) => {
  const active = columns.filter((c) => !blank((filters || {})[c.key]));
  if (!active.length) return rows;
  return rows.filter((row) => active.every((c) => {
    const have = String(c.text(row) ?? '').toLowerCase();
    const want = String(filters[c.key]).trim().toLowerCase();
    return c.options ? have === want : have.includes(want);
  }));
};

// The next sort when a header is clicked: ascending, then descending, then the original order.
export const nextSort = (sort, key) => {
  if (!sort || sort.key !== key) return { key, dir: 'asc' };
  return sort.dir === 'asc' ? { key, dir: 'desc' } : null;
};

// The different values of an options column: in the column's own `order` (a list of values) when it has one, else alphabetical.
export const optionsOf = (rows, column) => {
  const values = [...new Set(rows.map((r) => String(column.text(r) ?? '')).filter(Boolean))];
  const rank = (v) => (column.order && column.order.includes(v) ? column.order.indexOf(v) : column.order ? column.order.length : 0);
  return values.sort((a, b) => rank(a) - rank(b) || compare(a, b));
};
